<script setup lang="ts">
/**
 * @name EstadoCuenta.vue
 * @version 1.0.0
 * @description Componente para mostrar el estado de cuenta de una institución
 * @author @Jorel254
 * @changelog 1.0.0 - 2026/01/30 - @Jorel254 - Versión inicial
 */
import { EPlan } from '@shared/types/enums'
import { BitacoraData } from '@shared/types/base'
import { descargarAcuseConsumo } from '@/components/utils/acuseUtils'
import { es } from 'date-fns/locale/es'
import { ref } from 'vue'
import { useStore } from 'vuex'
const store = useStore()
const props = defineProps<{
	planActual: { value: EPlan; text: string }
	bitacora: BitacoraData | null
	currentInstitution: string
}>()
const colorVigencia: Record<EPlan, string> = {
	[EPlan.POSTPAID]: '#DEF5C9',
	[EPlan.PREPAID]: '#0C8EC2',
	[EPlan.UNLIMITED]: '#EAC90C',
	[EPlan.BASIC]: '#DEF5C9'
}
const classCardDates: Record<EPlan, string> = {
	[EPlan.POSTPAID]: 'date-card-basic',
	[EPlan.PREPAID]: 'date-card-prepaid',
	[EPlan.UNLIMITED]: 'date-card-unlimited',
	[EPlan.BASIC]: 'date-card-basic'
}
const fechaDelEstadoDeCuenta = ref({ start: new Date(), end: new Date(new Date().setDate(new Date().getDate() + 7)) })
const descargar = async () => {
	await descargarAcuseConsumo(props.planActual.value, props.currentInstitution, props.bitacora, fechaDelEstadoDeCuenta.value).catch(error => {
		console.error('Error al descargar estado de cuenta:', error)
		store.dispatch('notification/showError', error.message)
	})
}
</script>
<template>
	<VaCard class="feature-card card-spacing">
		<VaCardContent class="feature-card__content">
			<div class="plan-card__list">
				<VaAlert :color="props.planActual ? colorVigencia[props.planActual.value] : undefined" center class="vigencia-alert">
					<strong class="vigencia-alert__text">Obtener estado de cuenta </strong>
				</VaAlert>
			</div>
			<div>
				<VaCard :class="props.planActual ? classCardDates[props.planActual.value] : undefined" flat>
					<VaCardContent class="date-content">
						<div class="date-text">
							<div class="balance-content">
								<div class="balance-header">
									<div class="row">
										<div class="col-md-2">
											<div class="date-icon-wrapper">
												<VaIcon name="receipt_long" size="large" class="price-icon" />
											</div>
										</div>
										<div class="col-md-10">
											<h3 class="balance-title">Estado de cuenta</h3>
											<p class="balance-subtitle">Descarga el estado de cuenta de la institución por mes</p>
										</div>
									</div>
								</div>
								<div class="balance-actions">
									<div class="balance-date-section">
										<label class="balance-date-label" for="bitacora-fecha-fin">Seleccionar mes</label>
										<div class="balance-date-wrapper">
											<VaDatePicker id="bitacora-fecha-fin" v-model="fechaDelEstadoDeCuenta" :locale="es" inline mode="range" class="balance-date-picker" auto-apply />
										</div>
									</div>
									<VaButton icon="download" class="descargar-acuse-consumo" color="success" size="large" @click="descargar"> Descargar estado de cuenta </VaButton>
								</div>
							</div>
						</div>
					</VaCardContent>
				</VaCard>
			</div>
		</VaCardContent>
	</VaCard>
</template>
<style scoped>
.descargar-acuse-consumo {
	border: none;
	border-radius: 10px;
	font-weight: 600;
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
	box-shadow: 0 2px 8px rgba(34, 197, 94, 0.35);
	flex-shrink: 0;
}
.descargar-acuse-consumo:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 14px rgba(34, 197, 94, 0.45);
}
.balance-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	width: 100%;
}
.balance-header {
	margin-bottom: 0.25rem;
}
.balance-title {
	font-size: 1.375rem;
	font-weight: 700;
	color: rgba(0, 0, 0, 0.92);
	margin: 0 0 0.35rem 0;
	letter-spacing: -0.02em;
	line-height: 1.3;
}
.balance-subtitle {
	font-size: 0.9375rem;
	color: rgba(0, 0, 0, 0.72);
	margin: 0;
	font-weight: 500;
	line-height: 1.45;
}
.balance-actions {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
	gap: 1.25rem;
}
@media (max-width: 480px) {
	.balance-actions {
		flex-direction: column;
		width: 100%;
	}
	.descargar-acuse-consumo {
		width: 100%;
	}
	.balance-date-section {
		min-width: 100%;
	}
}
.balance-date-section {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	min-width: 200px;
}
.balance-date-label {
	font-size: 0.8125rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.8);
	letter-spacing: 0.02em;
}
.balance-date-wrapper {
	background: rgba(255, 255, 255, 0.4);
	border-radius: 10px;
	padding: 0.5rem;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.balance-date-wrapper :deep(.dp__main) {
	border: none;
	border-radius: 8px;
}
.balance-date-wrapper :deep(.dp__inner) {
	border: none;
}

.balance-value {
	font-size: 3.5rem;
	font-weight: 800;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -1px;
	margin-bottom: 1.5rem;
	transition: all 0.3s ease;
}
/* Contenedor principal */
.plan-features {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	width: 100%;
	padding: 1.5rem;
	border-radius: 16px;
	gap: 0;
}

/* Card principal del plan (header con color) */
.plan-card {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	width: 100%;
	padding: 1.75rem;
	border-radius: 16px;
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 10px 24px -4px rgba(0, 0, 0, 0.15);
	transition:
		transform 0.25s ease,
		box-shadow 0.25s ease;
	overflow: hidden;
	position: relative;
}

.plan-card::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background: rgba(255, 255, 255, 0.35);
	opacity: 0;
	transition: opacity 0.25s ease;
}

.plan-card:hover {
	transform: translateY(-2px);
	box-shadow:
		0 8px 12px -2px rgba(0, 0, 0, 0.12),
		0 16px 32px -6px rgba(0, 0, 0, 0.18);
}

.plan-card:hover::before {
	opacity: 1;
}

.plan-card--teal {
	background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
}

.plan-card--blue {
	background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.plan-card--orange {
	background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
}

.plan-card--purple {
	background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
}

.plan-card__title {
	margin: 0 0 1rem;
	font-size: 1.5rem;
	font-weight: 700;
	color: #fff;
	text-align: center;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

/* Cards internas (contenido blanco) */
.feature-card {
	margin-bottom: 1rem;
	border-radius: 12px;
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	transition:
		box-shadow 0.2s ease,
		border-color 0.2s ease;
	overflow: hidden;
}

.feature-card:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	border-color: rgba(0, 0, 0, 0.08);
}

.feature-card__content {
	padding: 1.25rem 1.5rem;
}

.card-spacing {
	margin-bottom: 1rem;
}

/* Alerta de vigencia */
.vigencia-alert {
	margin-bottom: 1.25rem;
	border-radius: 10px;
	border: none;
	box-shadow: 0 1px 3px rgba(13, 148, 136, 0.2);
}

.vigencia-alert__text {
	font-size: 1.125rem;
	font-weight: 700;
	letter-spacing: 0.02em;
}

/* Grid de fechas (Inicio / Fin) */
.dates-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 1rem;
}

@media (max-width: 640px) {
	.dates-grid {
		grid-template-columns: 1fr;
	}
}

/* Cards de fecha (Inicio / Fin del contrato) */
.date-card-basic {
	border-radius: 12px;
	background: linear-gradient(135deg, #fdba74 0%, #fd9443 100%) !important;
	border: none;
	box-shadow: 0 2px 8px rgba(237, 124, 58, 0.35);
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
	overflow: hidden;
}
.date-card-unlimited {
	border-radius: 12px;
	background: linear-gradient(135deg, #f1dd6c 0%, #eac90c 100%) !important;
	border: none;
	box-shadow: 0 2px 8px rgba(237, 124, 58, 0.35);
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
	overflow: hidden;
}
.date-card-prepaid {
	border-radius: 12px;
	background: linear-gradient(135deg, #d4d1e3 0%, #9e88f5 100%) !important;
	border: none;
	box-shadow: 0 2px 8px rgba(237, 124, 58, 0.35);
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
	overflow: hidden;
}

.date-content {
	display: flex;
	align-items: center;
	gap: 1rem;
	padding: 1.25rem;
	position: relative;
}

.date-icon-wrapper {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 52px;
	height: 52px;
	background: rgba(255, 255, 255, 0.25);
	border-radius: 14px;
	backdrop-filter: blur(8px);
	position: relative;
	z-index: 1;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.price-icon {
	color: #fff;
	font-size: 1.5rem;
}

.date-text {
	flex: 1;
	min-width: 0;
	position: relative;
	z-index: 1;
}

.date-title {
	font-size: 1rem;
	font-weight: 700;
	margin: 0 0 0.35rem;
	color: rgba(0, 0, 0, 0.9);
	text-align: left;
}

.date-message {
	font-size: 0.9375rem;
	margin: 0;
	color: rgba(0, 0, 0, 0.85);
	line-height: 1.5;
	font-weight: 500;
}

.plan-card__description {
	margin: 0 0 1rem;
	font-size: 0.875rem;
	color: rgba(255, 255, 255, 0.95);
	line-height: 1.45;
}

.plan-card__list {
	margin: 0 0 1.25rem;
	padding: 0;
	list-style: none;
	flex-grow: 1;
}

.plan-card__item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 0.5rem;
	font-size: 0.875rem;
	color: #fff;
}

.plan-card__item::before {
	content: '';
	display: inline-block;
	width: 1rem;
	height: 1rem;
	flex-shrink: 0;
	background-size: contain;
	background-repeat: no-repeat;
	background-position: center;
}

.plan-card__item--included::before {
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E");
	background-color: #22c55e;
	border-radius: 4px;
}

.plan-card__item--excluded::before {
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E");
	background-color: #dc2626;
	border-radius: 4px;
}

.plan-card__cta {
	display: block;
	width: 100%;
	padding: 0.75rem 1rem;
	font-size: 0.875rem;
	font-weight: 700;
	color: #fff;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	background: rgba(0, 0, 0, 0.2);
	border: none;
	border-radius: 8px;
	cursor: pointer;
	transition: background 0.2s ease;
}

.plan-card__cta:hover {
	background: rgba(0, 0, 0, 0.3);
}
</style>
